import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Chip,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useSnackbar } from "notistack";
import { useWhatsappTemplates } from "../../hooks/usePlanSummary";
import { useWhatsappNumbers } from "../../hooks/useWhatsapp";
import { countBodyVariables, bodyPreview, isSendableTemplate } from "../../utils/whatsappTemplate";

// Same 500-per-request cap the backend enforces on every bulk send — chunk so a bigger
// selection doesn't just 400 outright.
const CHUNK_SIZE = 500;

// Same shape the backend falls back to itself when campaignName is omitted
// (helpers/whatsappHelper.js recordPlanSendCampaign) — pre-fills the field, editable before sending.
function defaultCampaignName(planName) {
  const today = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  return planName ? `${planName} — ${today}` : "";
}

const plural = (n, noun) => `${n} ${noun}${n === 1 ? "" : "s"}`;

// The bulk WhatsApp template send dialog shared by the Plans page (plans/WhatsAppBulkSendModal) and
// the Script / Video boards (approvals/ApprovalWhatsAppSendModal): number, template, variables,
// preview, opt-in and the sent/failed result. Callers only supply what differs:
//   recipientIds          ids sent to `sendChunk`, chunked by CHUNK_SIZE
//   noun                  "influencer" / "creator", for the title and messages
//   onOptIn()             records WhatsApp consent for the recipients
//   sendChunk(ids, body)  one request; body = { templateId, variables, campaignName } → { sent, failed }
//   initialVariables(template, count)   optional pre-filled variable values
//   onVariableChange(i, value, template) optional side effect of typing in a variable
//   variableAdornment(i, template, variableCount, setVariableAt)  optional end adornment per field
//   tokens                optional [{ token, label }] chips inserted into the last-focused variable
//   toPreviewVariables(variables)  optional — what the preview shows for the typed variables
function WhatsAppTemplateSendDialog({
  open,
  onClose,
  planName,
  recipientIds,
  noun,
  onOptIn,
  sendChunk,
  initialVariables,
  onVariableChange,
  variableAdornment,
  tokens,
  toPreviewVariables,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: templatesData, isLoading: templatesLoading } = useWhatsappTemplates("APPROVED");
  const { data: numbersData } = useWhatsappNumbers();

  const [wabaId, setWabaId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [variables, setVariables] = useState([]);
  const [campaignName, setCampaignName] = useState("");
  const [optInConfirmed, setOptInConfirmed] = useState(false);
  const [recordingOptIn, setRecordingOptIn] = useState(false);
  const [focusedVar, setFocusedVar] = useState(0);
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);

  // Pre-filled once when the dialog opens (not on every render).
  useEffect(() => {
    if (open) setCampaignName(defaultCampaignName(planName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const numbers = (numbersData?.tokens || []).filter((n) => n.isActive);
  const selectedNumber = numbers.find((n) => n.wabaId === wabaId) || null;
  const recipientCount = recipientIds.length;
  // Meta's messaging-limit tiers count DISTINCT customers reached outside a customer service window
  // in a rolling 24h — headroom is limit minus unique people already reached, not messages sent.
  const wouldExceedLimit =
    selectedNumber && recipientCount > selectedNumber.messagingLimit - selectedNumber.recentUniqueRecipients;
  // A template belongs to one number's WABA — picking the number first answers "which number does
  // this go out from", since two numbers can both have a template of the same name.
  const templates = useMemo(() => {
    const all = templatesData?.templates || [];
    return (wabaId ? all.filter((t) => t.wabaId === wabaId) : all).filter(isSendableTemplate);
  }, [templatesData, wabaId]);
  const selectedTemplate = useMemo(() => templates.find((t) => t._id === templateId) || null, [templates, templateId]);
  const variableCount = useMemo(() => countBodyVariables(selectedTemplate), [selectedTemplate]);
  const previewVariables = useMemo(
    () => (toPreviewVariables ? toPreviewVariables(variables) : variables),
    [toPreviewVariables, variables]
  );

  // Default to the first number once the list loads — a single-number setup needs no extra click.
  useEffect(() => {
    if (!wabaId && numbers.length > 0) setWabaId(numbers[0].wabaId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numbers.length]);

  const handleSelectNumber = (id) => {
    setWabaId(id);
    setTemplateId("");
    setVariables([]);
    setOptInConfirmed(false);
  };

  const handleSelectTemplate = (template) => {
    setTemplateId(template?._id || "");
    const count = countBodyVariables(template);
    setVariables(initialVariables?.(template, count) || new Array(count).fill(""));
    setFocusedVar(0);
    setResult(null);
  };

  const setVariableAt = (i, value) => {
    setVariables((prev) => {
      const next = [...prev];
      next[i] = value;
      return next;
    });
  };

  const handleClose = () => {
    if (sending) return;
    setTemplateId("");
    setVariables([]);
    setCampaignName("");
    setOptInConfirmed(false);
    setResult(null);
    onClose();
  };

  const handleConfirmOptIn = async (e) => {
    const checked = e.target.checked;
    setOptInConfirmed(checked);
    if (!checked) return; // unchecking doesn't retract already-recorded consent
    setRecordingOptIn(true);
    try {
      await onOptIn();
    } catch (error) {
      setOptInConfirmed(false);
      enqueueSnackbar(error?.response?.data?.message || "Failed to record opt-in", { variant: "error" });
    } finally {
      setRecordingOptIn(false);
    }
  };

  const handleSend = async () => {
    if (!templateId || !recipientCount) return;
    const body = { templateId, variables, campaignName: campaignName.trim() };

    setSending(true);
    const sent = [];
    const failed = [];
    try {
      // One chunk at a time: each request checks the number's messaging limit against what is
      // already sent, which parallel requests would all read before any of them had sent.
      for (let i = 0; i < recipientCount; i += CHUNK_SIZE) {
        const res = await sendChunk(recipientIds.slice(i, i + CHUNK_SIZE), body);
        sent.push(...(res.sent || []));
        failed.push(...(res.failed || []));
      }
      setResult({ sent, failed });
      if (failed.length === 0) enqueueSnackbar(`Sent to ${plural(sent.length, noun)}`, { variant: "success" });
    } catch (error) {
      // Earlier chunks may already have gone out — show them rather than hiding a partial send.
      if (sent.length || failed.length) setResult({ sent, failed });
      enqueueSnackbar(error?.response?.data?.message || "Failed to send messages", { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        <WhatsAppIcon sx={{ color: "#25D366" }} />
        Send WhatsApp message to {plural(recipientCount, noun)}
      </DialogTitle>
      <DialogContent>
        {templatesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : numbers.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            No WhatsApp number registered yet.
          </Alert>
        ) : !result ? (
          <>
            <TextField
              fullWidth
              size="small"
              label="Campaign name"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              helperText="Tracked on the WhatsApp Campaigns page — pre-filled, but edit or clear it for your own name."
              sx={{ mt: 1, mb: 2 }}
            />

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="wa-number-label">WhatsApp Number</InputLabel>
              <Select labelId="wa-number-label" label="WhatsApp Number" value={wabaId} onChange={(e) => handleSelectNumber(e.target.value)}>
                {numbers.map((n) => (
                  <MenuItem key={n.wabaId} value={n.wabaId}>
                    {n.displayPhoneNumber}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedNumber && (
              <Typography sx={{ fontSize: 11.5, color: wouldExceedLimit ? "#C0392B" : "#9CA3AF", mb: 2, mt: -1 }}>
                {selectedNumber.recentUniqueRecipients}/{selectedNumber.messagingLimit} unique recipients reached on this
                number in the last 24h
                {wouldExceedLimit && ` — sending to ${recipientCount} more would exceed the remaining headroom; the excess will be skipped, not silently over-sent`}
              </Typography>
            )}

            {templates.length === 0 ? (
              <Alert severity="info">
                No approved WhatsApp templates for this number yet. WhatsApp only allows messaging someone who hasn't
                texted us first via a pre-approved template — create one on the Templates page and wait for Meta's
                review (usually minutes, sometimes up to 24h) before it shows up here.
              </Alert>
            ) : (
              <Autocomplete
                size="small"
                fullWidth
                options={templates}
                value={selectedTemplate}
                onChange={(_, t) => handleSelectTemplate(t)}
                getOptionLabel={(t) => `${t.name} (${t.language})`}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                noOptionsText="No matching template"
                renderInput={(params) => <TextField {...params} label="Template" placeholder="Search by name" />}
              />
            )}

            {selectedTemplate && (
              <Box sx={{ mt: 2 }}>
                {Array.from({ length: variableCount }).map((_, i) => {
                  const adornment = variableAdornment?.(i, selectedTemplate, variableCount, setVariableAt);
                  return (
                    <TextField
                      key={i}
                      fullWidth
                      size="small"
                      label={`Variable {{${i + 1}}}`}
                      value={variables[i] || ""}
                      onFocus={() => setFocusedVar(i)}
                      onChange={(e) => {
                        setVariableAt(i, e.target.value);
                        onVariableChange?.(i, e.target.value, selectedTemplate);
                      }}
                      sx={{ mb: 1.5 }}
                      InputProps={adornment ? { endAdornment: adornment } : undefined}
                    />
                  );
                })}

                {tokens?.length > 0 && variableCount > 0 && (
                  <>
                    <Typography sx={{ fontSize: 11.5, color: "#9CA3AF", mb: 0.75 }}>
                      Insert a token into the last-focused variable — each {noun} gets their own value.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
                      {tokens.map((t) => (
                        <Chip
                          key={t.token}
                          size="small"
                          label={t.label}
                          onClick={() => {
                            const i = Math.min(focusedVar, variableCount - 1);
                            setVariableAt(i, `${variables[i] || ""}${t.token}`);
                          }}
                          sx={{ fontSize: 11.5 }}
                        />
                      ))}
                    </Box>
                  </>
                )}

                <Divider sx={{ my: 1.5 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>
                  Preview
                </Typography>
                <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#F3FDF6", border: "1px solid #C8F0D4", fontSize: 13.5, mb: 1.5, overflowWrap: "anywhere" }}>
                  {bodyPreview(selectedTemplate, previewVariables) || "—"}
                </Box>

                <FormControlLabel
                  control={<Checkbox size="small" checked={optInConfirmed} disabled={recordingOptIn} onChange={handleConfirmOptIn} />}
                  label={
                    <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
                      I confirm these {plural(recipientCount, "creator")} have opted in to receive WhatsApp messages from
                      us — checking this records consent for their number{recipientCount === 1 ? "" : "s"}; without it
                      the send will be blocked. Messaging without real consent drives up block rate, which is what
                      actually risks this number's quality rating and messaging limit.
                    </Typography>
                  }
                />
                {recordingOptIn && <Typography sx={{ fontSize: 11.5, color: "#9CA3AF", mt: 0.5 }}>Recording consent…</Typography>}
              </Box>
            )}
          </>
        ) : (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {result.sent.length > 0 && <Alert severity="success">Sent to {plural(result.sent.length, noun)}.</Alert>}
            {result.failed.length > 0 && (
              <Alert severity="warning">
                {result.failed.length} failed:
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {result.failed.map((f) => (
                    <li key={f.influencerId}>
                      {f.name} — {f.reason}
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
              This send is tracked as a WhatsApp Campaign — see delivered/read/replied status per creator on the{" "}
              <a href="/whatsapp/campaigns" target="_blank" rel="noreferrer">
                WhatsApp Campaigns
              </a>{" "}
              page.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none", borderRadius: "10px" }} disabled={sending}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && templates.length > 0 && (
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={!templateId || !optInConfirmed || recordingOptIn || sending}
            sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none", bgcolor: "#25D366", "&:hover": { bgcolor: "#1FAF57" } }}
          >
            {sending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Send"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default WhatsAppTemplateSendDialog;
